import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleIcon from "@mui/icons-material/People";
import "./TableDocumentsReport.css";

interface ReportRow {
  id: string;
  documentName: string;
  ownerName: string;
  ownerEmail: string;
  status: string;
  signType: string;
  createdDate: string;
  completedDate: string;
  lastActivityDate: string;
  signedSigners: number;
  totalSigners: number;
  pages: number;
  size: string;
  signerNames: string[];
  signerEmails: string[];
}

interface TableDocumentsReportProps {
  rows: ReportRow[];
  viewDocument: (id: string) => void;
  downloadDocument: (id: string) => void;
}

const getStatusChip = (status: string) => {
  const statusConfig: Record<string, { color: "success" | "warning" | "error" | "info" | "default"; label: string }> = {
    "None": { color: "default", label: "None" },
    "Assinado": { color: "success", label: "Assinado" },
    "Finalizado": { color: "success", label: "Finalizado" },
    "Aguardando Signatários": { color: "warning", label: "Aguardando" },
    "Aguardando Assinaturas": { color: "info", label: "Pendente" },
    "Modificado": { color: "info", label: "Modificado" },
    "Em Processamento": { color: "info", label: "Processando" },
    "Cancelado": { color: "error", label: "Cancelado" },
  };
  const config = statusConfig[status] || { color: "default" as const, label: status };
  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      className="report-status-chip"
    />
  );
};

const TableDocumentsReport: React.FC<TableDocumentsReportProps> = ({
  rows,
  viewDocument,
  downloadDocument,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, rowId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const selectedRow = rows.find((r) => r.id === selectedRowId);
  const canDownload = selectedRow && (selectedRow.status === "Assinado" || selectedRow.status === "Finalizado");

  return (
    <TableContainer component={Paper} className="report-table-container">
      <Table className="report-table" aria-label="Relatório de documentos" size="medium">
        <TableHead>
          <TableRow className="report-header-row">
            <TableCell className="report-header-cell" align="left">ID</TableCell>
            <TableCell className="report-header-cell" align="left">Documento</TableCell>
            <TableCell className="report-header-cell" align="left">Proprietário</TableCell>
            <TableCell className="report-header-cell" align="left">E-mail Proprietário</TableCell>
            <TableCell className="report-header-cell" align="center">Status</TableCell>
            <TableCell className="report-header-cell" align="center">Tipo</TableCell>
            <TableCell className="report-header-cell" align="center">Criado em</TableCell>
            <TableCell className="report-header-cell" align="center">Concluído em</TableCell>
            <TableCell className="report-header-cell" align="center">Última atividade</TableCell>
            <TableCell className="report-header-cell" align="center">Assinaturas</TableCell>
            <TableCell className="report-header-cell" align="center">Págs</TableCell>
            <TableCell className="report-header-cell" align="center">Tamanho</TableCell>
            <TableCell className="report-header-cell" align="center">Signatários</TableCell>
            <TableCell className="report-header-cell report-actions-header" align="center">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} className="report-row" hover>
              <TableCell className="report-id-cell" align="left">
                <Tooltip title={row.id} arrow placement="top-start">
                  <span className="report-document-id">{row.id.substring(0, 8)}...</span>
                </Tooltip>
              </TableCell>
              <TableCell className="report-document-name-cell" align="left">
                <span className="report-document-name" title={row.documentName}>
                  {row.documentName}
                </span>
              </TableCell>
              <TableCell align="left">{row.ownerName}</TableCell>
              <TableCell align="left">
                <span className="report-email" title={row.ownerEmail}>
                  {row.ownerEmail}
                </span>
              </TableCell>
              <TableCell align="center">{getStatusChip(row.status)}</TableCell>
              <TableCell align="center">
                <span className="report-sign-type">{row.signType}</span>
              </TableCell>
              <TableCell align="center" className="report-date-cell">{row.createdDate}</TableCell>
              <TableCell align="center" className="report-date-cell">{row.completedDate}</TableCell>
              <TableCell align="center" className="report-date-cell">{row.lastActivityDate}</TableCell>
              <TableCell className="report-signers-cell" align="center">
                <div className="report-signers-progress">
                  <span className={`report-progress-text ${row.signedSigners === row.totalSigners && row.totalSigners > 0 ? "complete" : ""}`}>
                    {row.signedSigners}/{row.totalSigners}
                  </span>
                  <div className="report-progress-bar">
                    <div
                      className="report-progress-fill"
                      style={{
                        width: row.totalSigners > 0 ? `${(row.signedSigners / row.totalSigners) * 100}%` : "0%",
                      }}
                    />
                  </div>
                </div>
              </TableCell>
              <TableCell align="center">{row.pages}</TableCell>
              <TableCell align="center">{row.size}</TableCell>
              <TableCell className="report-signers-list-cell" align="left">
                {row.signerNames?.length > 0 ? (
                  <Tooltip
                    title={
                      <div>
                        {row.signerNames.map((name, i) => (
                          <div key={i}>{name} ({row.signerEmails?.[i] || "-"})</div>
                        ))}
                      </div>
                    }
                    arrow
                    placement="left"
                  >
                    <span className="report-signers-trigger">
                      <PeopleIcon fontSize="small" /> {row.totalSigners} signatário(s)
                    </span>
                  </Tooltip>
                ) : (
                  <span className="report-signers-empty">-</span>
                )}
              </TableCell>
              <TableCell className="report-actions-cell" align="center">
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuClick(e, row.id)}
                  className="report-menu-button"
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl) && selectedRowId === row.id}
                  onClose={handleMenuClose}
                  className="report-actions-menu"
                >
                  <MenuItem
                    onClick={() => {
                      viewDocument(row.id);
                      handleMenuClose();
                    }}
                  >
                    <VisibilityIcon fontSize="small" className="report-menu-icon" />
                    Visualizar
                  </MenuItem>
                  {canDownload && (
                    <MenuItem
                      onClick={() => {
                        downloadDocument(row.id);
                        handleMenuClose();
                      }}
                    >
                      <DownloadIcon fontSize="small" className="report-menu-icon" />
                      Baixar
                    </MenuItem>
                  )}
                </Menu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableDocumentsReport;
